import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Form } from "../ui";
import { Mail, Key } from "lucide-react";

const verifyEmailSchema = z.object({
  code: z.string().min(5, "OTP must be 5 digits").max(5, "OTP must be 5 digits"),
  email: z.string().email().optional(),
  usage: z.enum(["VERIFY_EMAIL", "RESET_PASSWORD"]),
});

type VerifyEmailDTO = z.infer<typeof verifyEmailSchema>;

interface VerifyEmailFormProps {
  onSubmit: (data: VerifyEmailDTO) => Promise<void>;
  onResendOtp: () => Promise<void>;
  isPending: boolean;
  isResending: boolean;
}

export const VerifyEmailForm = ({
  onSubmit,
  onResendOtp,
  isPending,
  isResending,
}: VerifyEmailFormProps) => {
  const [otp, setOtp] = useState(["", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Get usage type from localStorage to determine the flow
  const usageType = localStorage.getItem("verificationUsage") || "VERIFY_EMAIL";
  const isPasswordReset = usageType === "RESET_PASSWORD";

  const form = useForm<VerifyEmailDTO>({
    resolver: zodResolver(verifyEmailSchema),
    defaultValues: {
      code: "",
      email: localStorage.getItem("pendingVerificationEmail") || "",
      usage: usageType as "VERIFY_EMAIL" | "RESET_PASSWORD",
    },
  });

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }

    // Update form value
    const otpString = newOtp.join("");
    form.setValue("code", otpString);

    // Auto-submit when all fields are filled
    if (otpString.length === 5) {
      const email = localStorage.getItem("pendingVerificationEmail");
      handleFormSubmit({
        code: otpString,
        email: email || undefined,
        usage: usageType as "VERIFY_EMAIL" | "RESET_PASSWORD",
      });
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text");
    const digits = pasteData.replace(/\D/g, "").slice(0, 5);

    if (digits.length === 5) {
      const newOtp = digits.split("");
      setOtp(newOtp);
      form.setValue("code", digits);
      // Auto-submit on paste
      const email = localStorage.getItem("pendingVerificationEmail");
      handleFormSubmit({
        code: digits,
        email: email || undefined,
        usage: usageType as "VERIFY_EMAIL" | "RESET_PASSWORD",
      });
    }
  };

  const handleFormSubmit = async (data: VerifyEmailDTO) => {
    try {
      const email = localStorage.getItem("pendingVerificationEmail");
      await onSubmit({
        ...data,
        email: email || undefined,
        usage: usageType as "VERIFY_EMAIL" | "RESET_PASSWORD",
      });
    } catch (error) {
      console.log(error);
      // Reset OTP on error
      setOtp(["", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResendClick = async () => {
    try {
      await onResendOtp();
      setTimeLeft(300); // Reset timer
      setOtp(["", "", "", "", ""]);
      form.setValue("code", "");
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className='space-y-6'>
      <div className='text-center'>
        <div className='mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4'>
          {isPasswordReset ? (
            <Key className='w-8 h-8 text-blue-600' />
          ) : (
            <Mail className='w-8 h-8 text-blue-600' />
          )}
        </div>
        <h1 className='text-2xl font-bold text-gray-900 mb-2'>
          {isPasswordReset ? "Reset your password" : "Check your email"}
        </h1>
        <p className='text-gray-600'>
          {isPasswordReset
            ? "We sent a 5-digit verification code to reset your password to "
            : "We sent a 5-digit verification code to "}
          <span className='font-medium text-gray-900'>
            {localStorage.getItem("pendingVerificationEmail")}
          </span>
        </p>
      </div>

      <Form.Root {...form}>
        <form onSubmit={form.handleSubmit(handleFormSubmit)} className='space-y-6'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-3 text-center'>
              Enter verification code
            </label>
            <div className='flex justify-center space-x-3'>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]'
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className='w-12 h-12 text-center text-xl font-semibold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors'
                  disabled={isPending}
                />
              ))}
            </div>
            <Form.Field
              control={form.control}
              name='code'
              render={() => (
                <Form.Item className='mt-2'>
                  <Form.Message />
                </Form.Item>
              )}
            />
          </div>

          <Button type='submit' className='w-full' loading={isPending} disabled={isPending}>
            {otp.join("").length === 5
              ? isPasswordReset
                ? "Verify & Continue"
                : "Verify Email"
              : `${isPasswordReset ? "Verify & Continue" : "Verify Email"} (${
                  otp.join("").length
                }/5)`}
          </Button>
        </form>
      </Form.Root>

      <div className='text-center space-y-3'>
        <p className='text-sm text-gray-600'>
          Code expires in{" "}
          <span className={`font-medium ${timeLeft < 60 ? "text-red-600" : "text-gray-900"}`}>
            {formatTime(timeLeft)}
          </span>
        </p>

        <div className='flex items-center justify-center space-x-1 text-sm'>
          <span className='text-gray-600'>Didn't receive the code?</span>
          <button
            type='button'
            onClick={handleResendClick}
            disabled={isResending || timeLeft > 240} // Allow resend after 1 minute
            className='text-blue-600 hover:text-blue-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed'
          >
            {isResending ? "Sending..." : "Resend"}
          </button>
        </div>
      </div>
    </div>
  );
};
